import React from "react";
import { NavbarDemo } from "./NavBar";
import { FileText, Scale, AlertCircle, CheckCircle, Mail } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <NavbarDemo />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <FileText className="h-8 w-8 text-green-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-gray-600">
            Last updated:{" "}
            {new Date().toLocaleDateString("en-US", {
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Agreement to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms of Service ("Terms") constitute a legally binding
              agreement between you ("User," "you," or "your") and BookaTherapy
              Inc. ("BookaTherapy," "we," "us," or "our") regarding your use of
              our platform, website, mobile application, and related services
              (collectively, the "Service"). By accessing or using our Service,
              you agree to be bound by these Terms. If you do not agree to these
              Terms, please do not use our Service.
            </p>
          </section>

          {/* Acceptance of Terms */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Acceptance of Terms
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed">
              By creating an account, booking an appointment, or using any
              aspect of our Service, you acknowledge that you have read,
              understood, and agree to be bound by these Terms and our Privacy
              Policy. You must be at least 18 years old to use our Service.
            </p>
          </section>

          {/* Description of Service */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Description of Service
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              BookaTherapy is an AI-powered platform that connects clients with
              licensed therapists and massage therapists. Our Service includes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>
                Matching clients with therapists based on preferences and needs
              </li>
              <li>Online appointment booking and scheduling</li>
              <li>Payment processing for therapy sessions</li>
              <li>Client account management</li>
              <li>Communication tools between clients and therapists</li>
              <li>Review and rating systems</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              We act as an intermediary platform and do not provide therapy
              services directly. All therapy services are provided by
              independent, licensed professionals.
            </p>
          </section>

          {/* User Accounts */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              User Accounts
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Account Creation
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  To use certain features of our Service, you must create an
                  account. You agree to provide accurate, current, and complete
                  information during registration and to update such information
                  to keep it accurate, current, and complete.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Account Security
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  You are responsible for maintaining the confidentiality of
                  your account credentials and for all activities that occur
                  under your account. You agree to notify us immediately of any
                  unauthorized use of your account.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Account Termination
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to suspend or terminate your account at
                  any time, with or without notice, for violation of these Terms
                  or for any other reason we deem appropriate.
                </p>
              </div>
            </div>
          </section>

          {/* Booking and Appointments */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <Scale className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Booking and Appointments
              </h2>
            </div>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Appointment Booking
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  When you book an appointment through our platform, you enter
                  into a direct agreement with the therapist or clinic.
                  BookaTherapy facilitates the booking but is not a party to the
                  service agreement between you and the therapist.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Cancellation and Refunds
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Cancellation and refund policies are governed by our{" "}
                  <a
                    href="/cancellation-policy"
                    className="text-blue-600 hover:underline">
                    Cancellation Policy
                  </a>
                  . Please review it carefully before booking.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No-Show Policy
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  If you fail to attend a scheduled appointment without proper
                  cancellation, you may be charged a no-show fee as outlined in
                  our Cancellation Policy.
                </p>
              </div>
            </div>
          </section>

          {/* Payments */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Payments
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Payment Processing
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  All payments are processed securely through third-party
                  payment processors. By providing payment information, you
                  authorize us to charge your payment method for all fees
                  associated with your use of the Service.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Pricing
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  All prices are displayed in Canadian dollars unless otherwise
                  stated. We reserve the right to change our pricing at any
                  time, but price changes will not affect appointments already
                  confirmed.
                </p>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  Refunds
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  Refund eligibility is determined by our Cancellation Policy
                  and the policies of individual therapists or clinics.
                  Processing fees may apply to refunds.
                </p>
              </div>
            </div>
          </section>

          {/* User Conduct */}
          <section>
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="h-6 w-6 text-orange-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                User Conduct
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Violate any laws, regulations, or third-party rights</li>
              <li>
                Impersonate any person or entity or misrepresent your
                affiliation
              </li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>
                Attempt to gain unauthorized access to any portion of the
                Service
              </li>
              <li>Harass, abuse, or harm other users or therapists</li>
              <li>Post false, misleading, or defamatory content</li>
              <li>
                Reverse engineer, decompile, or disassemble any part of the
                Service
              </li>
              <li>
                Use automated systems to access the Service without permission
              </li>
              <li>Share your account credentials with others</li>
            </ul>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Intellectual Property
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Service and its original content, features, and functionality
              are owned by BookaTherapy and are protected by international
              copyright, trademark, patent, trade secret, and other intellectual
              property laws.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You may not copy, modify, distribute, sell, or lease any part of
              our Service or included software, nor may you reverse engineer or
              attempt to extract the source code of that software, unless laws
              prohibit those restrictions or you have our written permission.
            </p>
          </section>

          {/* Disclaimers */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Disclaimers
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>Medical Disclaimer:</strong> BookaTherapy is not a
                healthcare provider. We do not provide medical advice,
                diagnosis, or treatment. Our Service is a platform that connects
                you with licensed therapists. Always consult with qualified
                healthcare professionals for medical concerns.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
              WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT
              NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR
              A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
            </p>
            <p className="text-gray-700 leading-relaxed">
              We do not guarantee that the Service will be uninterrupted,
              secure, or error-free, or that defects will be corrected. We are
              not responsible for the quality, safety, or legality of therapy
              services provided by therapists using our platform.
            </p>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Limitation of Liability
            </h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, BOOKATHERAPY SHALL NOT BE
              LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
              PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER
              INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE,
              GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Your use or inability to use the Service</li>
              <li>Any conduct or content of third parties on the Service</li>
              <li>
                Any unauthorized access to or use of our servers and/or any
                personal information stored therein
              </li>
              <li>
                Any interruption or cessation of transmission to or from the
                Service
              </li>
              <li>
                Any bugs, viruses, trojan horses, or the like that may be
                transmitted to or through the Service
              </li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Indemnification
            </h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to defend, indemnify, and hold harmless BookaTherapy and
              its officers, directors, employees, and agents from and against
              any claims, liabilities, damages, losses, and expenses, including
              reasonable legal fees, arising out of or in any way connected with
              your access to or use of the Service, your violation of these
              Terms, or your violation of any third-party right.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Termination
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We may terminate or suspend your account and access to the Service
              immediately, without prior notice or liability, for any reason,
              including if you breach these Terms. Upon termination, your right
              to use the Service will immediately cease. All provisions of these
              Terms that by their nature should survive termination shall
              survive termination.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Governing Law
            </h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with
              the laws of Canada, without regard to its conflict of law
              provisions. Any disputes arising from these Terms or your use of
              the Service shall be subject to the exclusive jurisdiction of the
              courts of Canada.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Changes to Terms
            </h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify or replace these Terms at any time.
              If a revision is material, we will provide at least 30 days'
              notice prior to any new terms taking effect. What constitutes a
              material change will be determined at our sole discretion. Your
              continued use of the Service after any changes constitutes
              acceptance of the new Terms.
            </p>
          </section>

          {/* Severability */}
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">
              Severability
            </h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is held to be invalid or
              unenforceable by a court, the remaining provisions will remain in
              effect. These Terms constitute the entire agreement between you
              and BookaTherapy regarding the Service.
            </p>
          </section>

          {/* Contact Information */}
          <section className="bg-green-50 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="h-6 w-6 text-green-600" />
              <h2 className="text-2xl font-semibold text-gray-900">
                Contact Us
              </h2>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms of Service, please
              contact us:
            </p>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Email:</strong>{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-blue-600 hover:underline">
                  info@bookatherapy.com
                </a>
              </p>
              <p>
                <strong>Phone:</strong>{" "}
                <a
                  href="tel:+17802677551"
                  className="text-blue-600 hover:underline">
                  +1 (780) 267-7551
                </a>
              </p>
              <p>
                <strong>Address:</strong> BookaTherapy Inc., Canada
              </p>
            </div>
          </section>
        </div>

        {/* Footer Note */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            By using our Service, you acknowledge that you have read,
            understood, and agree to be bound by these Terms of Service.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
